/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress,
  InputBase
} from '@material-ui/core'
import { GridCellEditCommitParams, GridCellParams } from '@material-ui/data-grid'
import { NumericFormat } from 'react-number-format'
import { CloudDownload, Edit } from '@material-ui/icons'
import { ServerError } from '../../../schemas/Error'
import UserContext from '../../../contexts/User'
import { UserContextType } from '../../../schemas/User'
import PageContainer from '../Common/PageContainer'
import ResponsiveList, { ResponsiveColumn } from '../Common/ResponsiveList'
import { urls } from '../../../urls'
import ItemService from '../../../services/Item'
import Utils from '../../../utils'
import permissions from '../../../permissions.json'

// -------------- Styles --------------
const useStyles = makeStyles((theme: Theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  loadMoreButton: {
    minHeight: 44,
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  loadingContainer: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    bottom: '0',
    right: '0',
    background: '#BFBFBFBF',
    opacity: 0.6,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '50vh'
  }
}))

// - in this part all the component texts are housed
const texts = {
  header: {
    description: 'Lista de productos'
  },
  body: {
    buttonLoadMore: {
      label: 'Cargar más ...'
    }
  }
}

function PriceEditCell (params: GridCellParams) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (params.hasFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [params.hasFocus])
  const displayValue = typeof params.value === 'number' || typeof params.value === 'string' ? params.value : ''
  return (
    <NumericFormat
      getInputRef={inputRef}
      value={displayValue}
      onValueChange={(values) => {
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: Utils.roundToMaxDecimals(values.floatValue ?? 0)
        })
      }}
      decimalScale={Utils.getMaxPriceDecimals()}
      thousandSeparator="."
      decimalSeparator=","
      prefix="COP "
      allowNegative={false}
      customInput={InputBase}
      fullWidth
      className="MuiDataGrid-editInputCell"
      inputProps={{ style: { textAlign: 'left' } }}
    />
  )
}

// widthPct: porcentaje del ancho de la tabla (las columnas visibles siempre suman 100%)
const columns: ResponsiveColumn[] = [
  {
    field: 'code',
    headerName: 'Código',
    widthPct: 15,
    editable: false,
  },
  {
    field: 'description',
    headerName: 'Producto',
    editable: true,
    widthPct: 45,
    cardTitle: true
  },
  {
    field: 'price',
    headerName: 'Precio',
    editable: true,
    widthPct: 25,
    valueFormatter: (params) => params.value != null && typeof params.value === 'number' ? Utils.formatPriceCOP(params.value) : '',
    renderEditCell: (params) => <PriceEditCell {...params} />
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 15,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

const columnsAdmin: ResponsiveColumn[] = [
  {
    field: 'code',
    headerName: 'Código',
    widthPct: 15,
    editable: false,
  },
  {
    field: 'description',
    headerName: 'Producto',
    editable: true,
    widthPct: 35,
    cardTitle: true
  },
  {
    field: 'account',
    headerName: 'Cuenta contable',
    editable: true,
    widthPct: 15,
    hideOnTablet: true
  },
  {
    field: 'price',
    headerName: 'Precio',
    editable: true,
    widthPct: 20,
    valueFormatter: (params) => params.value != null && typeof params.value === 'number' ? Utils.formatPriceCOP(params.value) : '',
    renderEditCell: (params) => <PriceEditCell {...params} />
  },
  {
    field: 'actions',
    headerName: 'Acciones',
    widthPct: 15,
    type: 'actions',
    renderCell: (params: any) => (
      <ActionButtons params={params} />
    )
  }
]

// ------------ Init state -----------
interface State {
  loadMoreButtonDisabled: boolean
  lastEvaluatedKey?: string
  items: any[]
  loading: boolean
}

const initState: State = {
  loadMoreButtonDisabled: false,
  items: [],
  loading: true
}

function ActionButtons(props: any) {
  const navigate = useNavigate()
  const [editPermission, setEditPermission] = useState<boolean>(false)
  const { userContext } = React.useContext(
    UserContext
  ) as UserContextType
  
  const onEdit = () => {
    navigate(`/${urls.app.index}/${urls.app.main.item.form}?document=${props.params.row.code ?? ''}`)
  }

  React.useEffect(() => {
    setEditPermission(userContext.permissions.some(permission => permission === permissions.item.update))
  }, [userContext])

  return (
    <IconButton
      type="submit"
      onClick={() => onEdit()}
      disabled={!editPermission}
    >
      <Edit fontSize={"small"} color={editPermission ? "primary" : "inherit"} />
    </IconButton>
  )
}

export default function ItemList() {

  const classes = useStyles()

  const [state, setState] = useState<State>(initState)
  const { userContext } = React.useContext(
    UserContext
  ) as UserContextType

  React.useEffect(() => {
    async function loadData() {
      const itemService = new ItemService()
      const response = await itemService.getItems({ limit: 10 })
      setState({
        ...state,
        items: response.items,
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true,
        loading: false
      })
    }
    loadData()
  }, [])

  const onLoadMore = async () => {
    try {
      const itemService = new ItemService()
      const response = await itemService.getItems({ limit: 10, lastEvaluatedKey: state.lastEvaluatedKey })
      setState({
        ...state,
        items: state.items.concat(response.items),
        lastEvaluatedKey: response.lastEvaluatedKey,
        loadMoreButtonDisabled: response.lastEvaluatedKey !== undefined ? false : true
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      }
    }
  }

  const gridEdit = async (params: GridCellEditCommitParams) => {
    try {
      // validations
      if (params.field === "account" && params.value && params.value.toString().length !== Number(process.env.REACT_APP_MAX_LENGTH_CONTABLE_ACCOUNT)) {
        throw new Error(`El número de cuenta contable debe ser de ${process.env.REACT_APP_MAX_LENGTH_CONTABLE_ACCOUNT} dígitos`)
      }
      let itemToEdit: any = {}

      const selectedItem = state.items.find(item => item.code === params.id)

      if (selectedItem !== undefined) {
        let value = params.value
        if (params.field === 'price') {
          if (typeof params.value === 'number') {
            value = Utils.roundToMaxDecimals(params.value)
          } else {
            const parsed = Utils.parseFormattedPriceCOP(String(params.value))
            value = parsed !== undefined ? Utils.roundToMaxDecimals(parsed) : 0
          }
        }
        itemToEdit = {
          ...selectedItem,
          [params.field]: value
        }
      } else {
        return
      }

      const itemService = new ItemService()
      const itemUpdated = await itemService.updateItem(itemToEdit)

      return toast.success(`El producto ${itemUpdated.code} fue editado con éxito`)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError && serverError.response) {
          return toast.error(serverError.response.data.message)
        }
      } else if (error instanceof Error) {
        return toast.error(error.message)
      }
    }
  }

  return (
    <PageContainer title={texts.header.description}>
      <Grid container spacing={3}>

        {/* ----- Listado: tabla (>= 600px) o cards (mobile) ------*/}
        <Grid item xs={12}>
          <ResponsiveList
            rows={state.items}
            columns={ userContext.permissions.some(p => p === permissions.super_admin) ? columnsAdmin : columns}
            keyId='code'
            gridEdit={gridEdit}
            cardActions={(row) => <ActionButtons params={{ row }} />}
          />
        </Grid>

        <Grid item xs={12} className={classes.alignRight}>
          <Button
            type="button"
            color="primary"
            className={classes.loadMoreButton}
            startIcon={<CloudDownload />}
            disabled={state.loadMoreButtonDisabled}
            onClick={() => onLoadMore()}
          >
            {texts.body.buttonLoadMore.label}
          </Button>
        </Grid>
      </Grid>
      {
        state.loading ?
        (
          <div className={classes.loadingContainer}>
            <Box>
              <CircularProgress/>
            </Box>
          </div>
        ) : ''
      }
    </PageContainer>
  )
}
